// ==========================================================
// Gourmet Mushroom Command Center — Type Definitions
// ==========================================================

/** Supported mushroom species */
export interface MushroomSpecies {
  id: string;
  name: string;           // Turkish name
  latinName: string;
  prefix: string;         // For auto-naming bags: LM, SH, IS, RE
  emoji: string;
  color: string;          // Tailwind color class
}

/** Real-time sensor reading from a single device */
export interface SensorReading {
  timestamp: string;
  temperature: number;
  humidity: number;
  co2: number;
  tvoc?: number;
  hcho?: number;
  pm25?: number;   // μg/m³
  pm10?: number;   // μg/m³
  aqi?: string;    // level_1 / level_2 etc.
  sensorBattery?: number; // % — MT15/MT29 sensör pil seviyesi
  receivedAt?: string;  // ISO — when this reading arrived (for "son güncelleme")
  tentId?: string; // 'tent-1' = çadır içi, 'tent-2' = çadır dışı
  // Psikrometrik türevler — psychrometrics.ts tarafından hesaplanır, opsiyonel
  vpd?: number;           // kPa — Vapour Pressure Deficit
  dewPoint?: number;      // °C — Çiy noktası
  absoluteHumidity?: number; // g/m³ — Mutlak nem
}

/** Status of a controllable device */
export interface DeviceStatus {
  id: string;
  name: string;
  type: 'fan_fae' | 'fan_circulation' | 'humidifier' | 'light' | 'heater' | 'cooler';
  source: 'tuya_plug' | 'inkbird_itc308' | 'inkbird_ihc200';
  isOnline: boolean;
  isActive: boolean;
  lastToggled: string;
  powerDraw?: number;
  automationEnabled: boolean;
  haEntityId?: string;   // e.g. "switch.tuya_fae_fan"
}

/** Threshold ranges for fruiting conditions */
export interface ThresholdRange {
  min: number;
  max: number;
  ideal: number;
  unit: string;
  criticalLow?: number;
  criticalHigh?: number;
}

export interface Thresholds {
  temperature: ThresholdRange;
  humidity: ThresholdRange;
  co2: ThresholdRange;
  vpd?: ThresholdRange;              // kPa
  dewPoint?: ThresholdRange;         // °C
  humidityDeficit?: ThresholdRange;  // g/m³
}

/** Computed health score for a given time window */
export interface HealthScore {
  overall: number;
  temperature: number;
  humidity: number;
  co2: number;
  vpd: number;
  dewPoint: number;
  humidityDeficit: number;
  label: 'Mükemmel' | 'İyi' | 'Dikkat' | 'Kritik';
  color: string;
  mode?: 'incubation' | 'fruiting';
  condensationRisk?: boolean;
}

/** Daily closing summary */
export interface DailySummary {
  date: string;
  dayNumber: number;
  avgTemp: number;
  minTemp: number;
  maxTemp: number;
  avgHumidity: number;
  minHumidity: number;
  maxHumidity: number;
  maxCo2: number;
  avgCo2: number;
  healthScore: number;
  alerts: number;
  faeActivations: number;
  activeBagCount?: number;  // for anomaly correlation
}

/** Alert/event log entry */
export interface AlertEvent {
  id: string;
  timestamp: string;
  severity: 'info' | 'warning' | 'critical';
  metric: 'temperature' | 'humidity' | 'co2' | 'sensor';
  message: string;
  value: number;
  threshold: number;
  resolved: boolean;
  source?: string;
}

/** Device on/off session log */
export interface DeviceEvent {
  id: string;
  deviceId: string;
  deviceName: string;
  deviceType: string;
  reason: string;           // why it was turned on/off
  startedAt: string;        // ISO — when turned ON
  endedAt?: string;         // ISO — when turned OFF (undefined = still running)
  durationMinutes?: number; // computed when OFF
}

/** Hourly breakdown for a specific past day */
export interface DayHourlyDetail {
  date: string;
  dayNumber: number;
  readings: SensorReading[];
  alerts: AlertEvent[];
}

/** Istanbul outdoor weather */
export interface OutdoorWeather {
  city: string;
  temperature: number;
  humidity: number;
  condition: string;
  dewPoint?: number;
  pressure?: number;
  windSpeed?: number;
  uvIndex?: number;
}

/** Ambient room reading — the room/space where the grow cabinet sits */
export interface AmbientReading {
  temperature: number;
  humidity: number;
  co2?: number;
  updatedAt: string; // HH:MM
}

/** Settings change log entry */
export interface SettingsLogEntry {
  id: string;
  timestamp: string;   // ISO
  category: 'climate' | 'be' | 'email';
  description: string; // human-readable summary
}

/** Extended climate history data point */
export interface DailyClimatePoint {
  date: string;
  avgTemp: number;
  avgHumidity: number;
  avgCo2: number;
  maxCo2: number;
  activeBagCount?: number;
}

/** Climate anomaly insight */
export interface ClimateInsight {
  date: string;
  type: 'co2_spike' | 'temp_high' | 'humidity_low' | 'bag_change';
  message: string;
  severity: 'info' | 'warning';
}

/** Date range preset */
export type DateRangePreset = '7d' | '14d' | '30d' | 'custom';

// ── Grow Bag System ──

export type BagStatus = 'incubation' | 'fruiting' | 'harvested' | 'retired';

/** Master mix ingredient */
export interface MasterMixIngredient {
  name: string;
  percentage: number;
  gramsCalculated?: number;
}

/** Master mix recipe — now species-aware */
export interface MasterMix {
  id: string;
  name: string;
  speciesId: string;        // which mushroom this recipe is for
  totalWeightKg: number;
  ingredients: MasterMixIngredient[];
  notes?: string;
  recipeTemplateId?: string;   // links to a built-in RecipeWizard template
  difficulty?: 'Başlangıç' | 'Orta' | 'İleri';
  provenBeMin?: number;        // historical min BE% from bags using this mix
  provenBeMax?: number;        // historical max BE%
}

/** Harvest quality grades */
export type HarvestGrade = 'premium' | 'standard' | 'processing';

export interface HarvestGradeBreakdown {
  premium: number;    // grams — 1st class / satışa hazır
  standard: number;  // grams — 2nd class
  processing: number; // grams — kurutma/işleme
}

/** Overall harvest quality rating (1-5 stars) */
export type HarvestQualityRating = 1 | 2 | 3 | 4 | 5;

/** Individual flush harvest record */
export interface FlushHarvest {
  flushNumber: number;
  harvestDate: string;
  yieldGrams: number;
  expectedYieldGrams?: number;    // pre-harvest target for accuracy tracking
  laborMinutes?: number;          // time spent on this flush (harvest + cleanup)
  qualityRating?: HarvestQualityRating;  // 1=poor, 3=good, 5=excellent
  qualityNote?: string;                  // free-text quality observation
  environmentSnapshot?: {         // sensor conditions at time of harvest
    temperature?: number;
    humidity?: number;
    co2?: number;
  };
  notes?: string;
  gradeBreakdown?: HarvestGradeBreakdown;
}

/** Loss / contamination record */
export type LossType = 'contamination' | 'quality_reject' | 'physical_damage' | 'environmental' | 'other';

export interface LossRecord {
  id: string;
  bagId: string;
  bagName: string;
  date: string;
  type: LossType;
  lossGrams?: number;
  notes?: string;
}

/** Photo log entry for a grow bag */
export interface PhotoLog {
  id: string;
  bagId: string;
  date: string;
  description: string;
  stage: BagStatus;
  imageEmoji: string; // mock: gerçek URL yerine emoji temsili
  imageUrl?: string;
  captureSource?: 'manual' | 'qr_place' | 'qr_remove' | 'timelapse' | 'change_detect' | 'field_round';
  linkedEventId?: string;
}

// ── Order Management ──

export type OrderStatus = 'pending' | 'confirmed' | 'processing' | 'fulfilled' | 'cancelled';
export type SalesChannel = 'online' | 'restaurant' | 'market' | 'direct';

export interface OrderItem {
  speciesId: string;
  quantityGrams: number;
  unitPricePerKg: number;
}

export interface Order {
  id: string;
  orderNumber: string;
  customerName: string;
  channel: SalesChannel;
  orderDate: string;
  deliveryDate: string;
  items: OrderItem[];
  status: OrderStatus;
  notes?: string;
}

// ── Task Management ──

export type TaskCategory = 'watering' | 'cleaning' | 'harvest_check' | 'inoculation' | 'climate_check' | 'packaging' | 'other';
export type TaskPriority = 'low' | 'medium' | 'high';

export interface Task {
  id: string;
  title: string;
  category: TaskCategory;
  dueDate: string;
  completed: boolean;
  bagId?: string;
  priority: TaskPriority;
  notes?: string;
  autoGenerated?: boolean;
}

/** Individual grow bag tracking */
export interface GrowBag {
  id: string;
  name: string;             // auto-generated: LM-001, SH-001
  speciesId: string;        // mushroom species
  masterMixId?: string;
  spawnLotId?: string;
  masterMix?: MasterMix;
  inoculationDate: string;
  fruitingStartDate?: string;
  pinDetectedDate?: string;   // when primordia first visible — tighter harvest window
  harvestDate?: string;
  status: BagStatus;
  position: string;
  bagWeightGrams?: number;
  notes: string;
  flushCount: number;
  flushHarvests: FlushHarvest[];
  totalYieldGrams: number;
  // Duration tracking (in days, auto-computed)
  incubationDays?: number;    // inoculation → fruiting start
  fruitingDays?: number;      // fruiting start → first harvest
  totalCycleDays?: number;    // inoculation → last harvest
  // Placement tracking
  shelf?: string;
  placedBy?: 'qr_scan' | 'manual';
  placedAt?: string;
  lastSeenAt?: string;
}

export type SpawnSource = 'self_grown' | 'purchased';

export interface SpawnLot {
  id: string;
  name: string;
  speciesId: string;
  source: SpawnSource;
  supplier?: string;
  productionDate: string;
  totalGrams: number;
  notes?: string;
}

/** QR code payload encoded into bag labels */
export interface QRPayload {
  id: string;
  speciesId: string;
  recipeId?: string;
  date: string;
  weight?: number;
}

/** Camera snapshot */
export interface CameraSnapshot {
  id: string;
  timestamp: string;
  imageUrl: string;
  bagId?: string;
}

/** Grow tent phase (legacy — kept for backward compat) */
export type TentPhase = 'fruiting' | 'incubation';

/** Cultivation stage — climate-control granular stages (3-stage cycle; harvest is an event, not a stage) */
export type CultivationStage = 'spawn_run' | 'pinning' | 'fruiting';

export const STAGE_LABELS: Record<CultivationStage, string> = {
  spawn_run: 'Kuluçka',
  pinning:   'Pinleme',
  fruiting:  'Meyveleme',
};

export const STAGE_EMOJI: Record<CultivationStage, string> = {
  spawn_run: '🥚',
  pinning:   '🌱',
  fruiting:  '🍄',
};

/** Grow tent configuration */
export interface TentConfig {
  id: string;
  name: string;         // e.g., "Çadır 1"
  phase: TentPhase;     // current phase
  dimensions?: string;  // e.g., "120×120×200"
}

/** Custom climate target settings (user-configurable) */
export interface ClimateSettings {
  tentId: string;
  phase: TentPhase;              // legacy
  stage: CultivationStage;       // active cultivation stage
  autoStageDetect: boolean;      // hybrid auto-suggest toggle
  temperature: { min: number; max: number; ideal: number };
  humidity: { min: number; max: number; ideal: number };
  co2: { min: number; max: number; ideal: number };
  vpd: { min: number; max: number; ideal: number };
  dewPoint: { min: number; max: number; ideal: number };
  humidityDeficit: { min: number; max: number; ideal: number };
}

/** BE (Biological Efficiency) reference values per species */
export interface BESpeciesSettings {
  low: number;      // % minimum expected
  avg: number;      // % average
  high: number;     // % high performer
  dryRatio: number; // substrate dry weight ratio (0–1)
}

export type BESettings = Record<string, BESpeciesSettings>;

export interface AlertSeverities {
  info: boolean;
  warning: boolean;
  critical: boolean;
}

export interface AlertChannelSettings {
  enabled: boolean;
  severities: AlertSeverities;
}

export interface AlertNotificationSettings {
  browser: AlertChannelSettings;
  email: AlertChannelSettings & { address: string };
  telegram: AlertChannelSettings;
  alertTypes: {
    climate: boolean;       // sıcaklık, nem, CO₂, VPD
    device: boolean;        // cihaz açık/kapalı
    watchdog: boolean;      // sensör bağlantı kesildi
    dailyReport: boolean;   // gün sonu raporu
    condensation: boolean;  // kondensasyon riski
  };
  quietHours: {
    enabled: boolean;
    startHour: number;      // 0-23, varsayılan 0 (gece yarısı)
    endHour: number;        // 0-23, varsayılan 6 (sabah 6)
    allowCritical: boolean; // kritik alarmlar gece modunda da geçsin mi
  };
}

/** @deprecated use AlertNotificationSettings */
export interface AlertEmailSettings {
  enabled: boolean;
  email: string;
  severities: AlertSeverities;
}

/** Per-device daily runtime log (mock data) */
export interface DeviceRuntimeDay {
  date: string;         // YYYY-MM-DD
  runtimeMinutes: number;
  activeBagCount: number;
}

// ── Sterilization Log ──

export type SterilizationMethod = 'pressure_cooker' | 'autoclave' | 'steam_pasteurization' | 'other';
export type SterilizationResult = 'success' | 'failed' | 'partial' | 'pending';

export interface SterilizationCyclePoint {
  minuteElapsed: number; // dakika
  temperatureCelsius: number;
}

export interface SterilizationLog {
  id: string;
  /** Hangi master mix / substrat partisi bu döngüde sterilize edildi */
  masterMixId?: string;
  masterMixName?: string;
  /** Bağlantılı torba ID'leri (inoküle sonrası) */
  linkedBagIds: string[];
  /** Sterilizasyon yöntemi */
  method: SterilizationMethod;
  /** Hedef sıcaklık (°C) */
  targetTempCelsius: number;
  /** Hedef süre (dakika) */
  targetDurationMin: number;
  /** Gerçek süre (dakika) */
  actualDurationMin?: number;
  /** Ulaşılan maksimum sıcaklık (°C) */
  maxTempReached?: number;
  /** Döngü başlangıcı */
  startedAt: string; // ISO
  /** Döngü bitişi */
  completedAt?: string; // ISO
  /** Otomatik doğrulama sonucu */
  result: SterilizationResult;
  /** Başarısızlık nedeni (result=failed ise) */
  failureReason?: string;
  /** Sıcaklık eğrisi (termokupl varsa, opsiyonel) */
  tempCurve?: SterilizationCyclePoint[];
  /** Substrat toplam ağırlık (kg) */
  substrateTotalKg?: number;
  /** Torba adedi */
  bagCount?: number;
  /** Notlar */
  notes?: string;
  createdAt?: string;
  stage?: string;
}

// ── Cleaning & Sanitation Log (GAP) ──

export type CleaningMethod = 'spray' | 'wipe' | 'fog' | 'full_wash';
export type CleaningArea = 'floor' | 'walls' | 'shelves' | 'equipment' | 'entrance' | 'air';

export interface CleaningLog {
  id: string;
  tentId: string;
  logDate: string;           // YYYY-MM-DD
  method: CleaningMethod;
  product?: string;          // e.g. "H2O2 %3", "70% Ethanol"
  concentration?: string;
  durationMin?: number;
  areasCleaned: CleaningArea[];
  notes?: string;
  nextDueDate?: string;      // YYYY-MM-DD
  createdAt?: string;
}
